import React, { useState } from 'react';
import { Search, Users, Video, DollarSign, Calendar, Mail, FileText, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const SearchHub = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [results, setResults] = useState({ contacts: [], videos: [], transactions: [], events: [], messages: [] });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setSearched(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // TODO: Implement backend unified search endpoint /api/search
      // For now, mock results
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockResults = {
        contacts: searchType === 'all' || searchType === 'contacts' ? [
          { id: 1, name: 'Drakos', type: 'Model', email: 'drakos@example.com' },
          { id: 2, name: 'Athens Underground', type: 'Studio', email: 'athens@example.com' }
        ] : [],
        videos: searchType === 'all' || searchType === 'videos' ? [
          { id: 1, title: 'Sample Video 1', duration: '12:34', platform: 'OnlyFans' }
        ] : [],
        transactions: searchType === 'all' || searchType === 'transactions' ? [] : [],
        events: searchType === 'all' || searchType === 'events' ? [] : [],
        messages: searchType === 'all' || searchType === 'messages' ? [] : []
      };
      
      setResults(mockResults);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTotalResults = () => {
    return Object.values(results).reduce((sum, arr) => sum + arr.length, 0);
  };

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Search size={32} className="text-purple-400" />
          <div>
            <h1 className="text-3xl font-bold">Search Hub</h1>
            <p className="text-zinc-400 text-sm">Unified search across all platforms and data</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="flex gap-3 mb-4">
            <div className="flex-1 relative">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search contacts, videos, transactions, events..."
                className="w-full pl-12 pr-4 py-4 bg-zinc-900 border border-zinc-800 rounded-xl text-lg text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={loading || !searchQuery.trim()}
              className="px-8 py-4 bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-800 disabled:text-zinc-600 rounded-xl font-medium transition inline-flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search size={20} />
                  Search
                </>
              )}
            </button>
          </div>

          {/* Search Type Filters */}
          <div className="flex gap-2">
            {[
              { value: 'all', label: 'All', icon: Search },
              { value: 'contacts', label: 'Contacts', icon: Users },
              { value: 'videos', label: 'Videos', icon: Video },
              { value: 'transactions', label: 'Transactions', icon: DollarSign },
              { value: 'events', label: 'Events', icon: Calendar },
              { value: 'messages', label: 'Messages', icon: Mail }
            ].map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.value}
                  onClick={() => setSearchType(type.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition inline-flex items-center gap-2 ${
                    searchType === type.value
                      ? 'bg-purple-600 text-white'
                      : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
                  }`}
                >
                  <Icon size={14} />
                  {type.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Results */}
        {searched && (
          <>
            {/* Results Summary */}
            <div className="mb-6 p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
              <p className="text-sm text-zinc-400">
                {loading ? (
                  'Searching...'
                ) : getTotalResults() === 0 ? (
                  `No results found for "${searchQuery}"`
                ) : (
                  `Found ${getTotalResults()} result${getTotalResults() > 1 ? 's' : ''} for "${searchQuery}"`
                )}
              </p>
            </div>

            {!loading && (
              <div className="space-y-6">
                {/* Contacts Results */}
                {results.contacts.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-zinc-800 flex items-center gap-2">
                      <Users size={20} className="text-blue-400" />
                      <h2 className="text-lg font-semibold">Contacts</h2>
                      <span className="ml-auto text-sm text-zinc-500">{results.contacts.length} found</span>
                    </div>
                    <div className="divide-y divide-zinc-800">
                      {results.contacts.map((contact) => (
                        <Link
                          key={contact.id}
                          to={`/crm/contacts`}
                          className="block p-4 hover:bg-zinc-800/50 transition"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <Users size={20} className="text-purple-400" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{contact.name}</p>
                              <p className="text-sm text-zinc-400">{contact.type} • {contact.email}</p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Videos Results */}
                {results.videos.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-zinc-800 flex items-center gap-2">
                      <Video size={20} className="text-purple-400" />
                      <h2 className="text-lg font-semibold">Videos</h2>
                      <span className="ml-auto text-sm text-zinc-500">{results.videos.length} found</span>
                    </div>
                    <div className="divide-y divide-zinc-800">
                      {results.videos.map((video) => (
                        <Link
                          key={video.id}
                          to={`/crm/catalog`}
                          className="block p-4 hover:bg-zinc-800/50 transition"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded bg-purple-500/20 flex items-center justify-center">
                              <Video size={20} className="text-purple-400" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{video.title}</p>
                              <p className="text-sm text-zinc-400">{video.duration} • {video.platform}</p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Results State */}
                {getTotalResults() === 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
                    <Search size={48} className="mx-auto mb-4 text-zinc-600" />
                    <h3 className="text-xl font-semibold mb-2">No Results Found</h3>
                    <p className="text-zinc-400 mb-4">
                      Try adjusting your search query or filters
                    </p>
                    <div className="inline-block p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg text-left text-sm">
                      <p className="text-blue-400 font-semibold mb-2">Backend Integration Required:</p>
                      <p className="text-zinc-400">
                        Implement unified search endpoint at <code className="px-1.5 py-0.5 bg-zinc-800 rounded text-purple-400">POST /api/search</code>
                      </p>
                      <p className="text-zinc-400 mt-1">
                        Should search across: contacts (CRM), videos (catalog), transactions (finance), events (calendar), messages (inbox)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Welcome State */}
        {!searched && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
            <Search size={64} className="mx-auto mb-4 text-zinc-600" />
            <h3 className="text-2xl font-semibold mb-2">Start Searching</h3>
            <p className="text-zinc-400 mb-6">
              Search across all your platforms, contacts, content, and data in one place
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-2xl mx-auto text-sm">
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <Users size={20} className="mx-auto mb-1 text-blue-400" />
                <p className="text-zinc-300">Contacts</p>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <Video size={20} className="mx-auto mb-1 text-purple-400" />
                <p className="text-zinc-300">Videos</p>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <DollarSign size={20} className="mx-auto mb-1 text-green-400" />
                <p className="text-zinc-300">Transactions</p>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <Calendar size={20} className="mx-auto mb-1 text-yellow-400" />
                <p className="text-zinc-300">Events</p>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <Mail size={20} className="mx-auto mb-1 text-red-400" />
                <p className="text-zinc-300">Messages</p>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <FileText size={20} className="mx-auto mb-1 text-cyan-400" />
                <p className="text-zinc-300">Documents</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchHub;
